// Enhanced ColumnsForm.js with HeroForm-style UI and Preview Section
import React, { useState } from 'react';
import { Plus, LayoutGrid, Image as ImageIcon } from 'lucide-react';
import ImageUploader from '../ImageUploader';
import ColorPicker from '../../shared/ColorPicker';

const ColumnsForm = ({ content, updateContent, pageSlug }) => {
  const [showPreview, setShowPreview] = useState(false);

  const addColumn = () => {
    const columns = content?.columns || [];
    const newColumns = [...columns, {
      title: '',
      content: '',
      imageUrl: '',
      width: '1fr',
      backgroundColor: '',
      textColor: '',
      padding: '',
      alignment: 'left'
    }];
    updateContent('columns', newColumns);
  };

  const updateColumn = (index, field, value) => {
    const columns = [...(content?.columns || [])];
    columns[index] = { ...columns[index], [field]: value };
    updateContent('columns', columns);
  };

  const removeColumn = (index) => {
    const columns = [...(content?.columns || [])];
    columns.splice(index, 1);
    updateContent('columns', columns);
  };

  return (
    <div className="max-w-6xl mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden">
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-white flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
            <LayoutGrid className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">Columns Section Builder</h2>
            <p className="text-indigo-100">Customize each column with ease</p>
          </div>
        </div>
        <button
          onClick={() => setShowPreview(!showPreview)}
          className="bg-white/20 hover:bg-white/30 transition-colors px-4 py-2 rounded-lg flex items-center gap-2"
        >
          {showPreview ? 'Hide' : 'Show'} Preview
        </button>
      </div>

      <div className="">
        <div className="flex-1 p-6 space-y-8">
          <div className="grid gap-6">
            <div className="bg-gray-50 p-5 rounded-xl border">
              <h3 className="font-semibold text-lg mb-4">Layout Settings</h3>
              <div className="grid gap-4 md:grid-cols-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Gap Between Columns</label>
                  <input
                    type="text"
                    className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500"
                    value={content?.columnGap || '1rem'}
                    onChange={(e) => updateContent('columnGap', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Columns Alignment</label>
                  <select
                    className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500"
                    value={content?.columnsAlignment || 'stretch'}
                    onChange={(e) => updateContent('columnsAlignment', e.target.value)}
                  >
                    <option value="stretch">Stretch</option>
                    <option value="flex-start">Top</option>
                    <option value="center">Center</option>
                    <option value="flex-end">Bottom</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Stack At (Mobile)</label>
                  <select
                    className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500"
                    value={content?.stackAt || 'sm'}
                    onChange={(e) => updateContent('stackAt', e.target.value)}
                  >
                    <option value="none">Don't stack</option>
                    <option value="xs">Extra small</option>
                    <option value="sm">Small (default)</option>
                    <option value="md">Medium</option>
                    <option value="lg">Large</option>
                    <option value="xl">Extra large</option>
                  </select>
                </div>
              </div>
            </div>

            {(content?.columns || []).map((column, index) => (
              <div key={index} className="p-5 bg-white rounded-xl border shadow-sm">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-semibold text-gray-700">Column {index + 1}</h4>
                  <button
                    className="text-red-500 text-sm hover:underline"
                    onClick={() => removeColumn(index)}
                  >Remove</button>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <input
                      type="text"
                      className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500"
                      value={column.title || ''}
                      onChange={(e) => updateColumn(index, 'title', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                    <textarea
                      className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500"
                      rows="3"
                      value={column.content || ''}
                      onChange={(e) => updateColumn(index, 'content', e.target.value)}
                    ></textarea>
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                    <ImageIcon className="w-4 h-4 text-indigo-500" /> Image URL
                  </label>
                  <input
                    type="text"
                    className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500"
                    value={column.imageUrl || ''}
                    onChange={(e) => updateColumn(index, 'imageUrl', e.target.value)}
                  />
                  {pageSlug && (
                    <div className="mt-2">
                      <ImageUploader
                        onImageUpload={(path) => updateColumn(index, 'imageUrl', path)}
                        pageSlug={pageSlug}
                        className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm"
                        buttonText="Upload Image"
                      />
                    </div>
                  )}
                  {column.imageUrl && (
                    <img
                      src={column.imageUrl}
                      alt={`Column ${index + 1}`}
                      className="w-full h-32 object-cover rounded-lg mt-3"
                    />
                  )}
                </div>
                <div className="grid gap-4 mt-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Column Width</label>
                    <select
                      className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500"
                      value={column.width || '1fr'}
                      onChange={(e) => updateColumn(index, 'width', e.target.value)}
                    >
                      <option value="1fr">1fr</option>
                      <option value="2fr">2fr</option>
                      <option value="3fr">3fr</option>
                      <option value="25%">25%</option>
                      <option value="33%">33%</option>
                      <option value="50%">50%</option>
                      <option value="66%">66%</option>
                      <option value="75%">75%</option>
                      <option value="100%">100%</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Text Color</label>
                    <ColorPicker
                      value={column.textColor || ''}
                      onChange={(color) => updateColumn(index, 'textColor', color)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Background Color</label>
                    <ColorPicker
                      value={column.backgroundColor || ''}
                      onChange={(color) => updateColumn(index, 'backgroundColor', color)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Padding</label>
                    <input
                      type="text"
                      className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500"
                      value={column.padding || ''}
                      onChange={(e) => updateColumn(index, 'padding', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Alignment</label>
                    <select
                      className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500"
                      value={column.alignment || 'left'}
                      onChange={(e) => updateColumn(index, 'alignment', e.target.value)}
                    >
                      <option value="left">Left</option>
                      <option value="center">Center</option>
                      <option value="right">Right</option>
                    </select>
                  </div>
                </div>
              </div>
            ))}

            <button
              className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-indigo-500 text-indigo-600 rounded-xl hover:bg-indigo-50 transition-all"
              onClick={addColumn}
            >
              <Plus className="w-5 h-5" /> Add Column
            </button>
          </div>
        </div>

        {showPreview && (
          <div className="bg-gray-100 p-6 rounded-xl mt-8">
            <div className="max-w-md mx-auto">
              <h3 className="font-bold text-lg mb-4 text-gray-800">Live Preview</h3>
              <div className="space-y-6">
                {(content?.columns || []).map((col, i) => (
                  <div
                    key={i}
                    className="p-4 rounded-xl shadow-sm"
                    style={{
                      backgroundColor: col.backgroundColor || '#f9fafb',
                      color: col.textColor || '#1f2937',
                      padding: col.padding || '1rem',
                      textAlign: col.alignment || 'left'
                    }}
                  >
                    {col.imageUrl && (
                      <img src={col.imageUrl} alt={col.title} className="w-full h-32 object-cover rounded mb-3" />
                    )}
                    <h4 className="text-lg font-semibold mb-1">{col.title}</h4>
                    <p className="text-sm">{col.content}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ColumnsForm;






// // src/components/admin/page-management/section-forms/ColumnsForm.js
// import React from 'react';
// import ImageUploader from '../ImageUploader';

// const ColumnsForm = ({ content, updateContent, pageSlug }) => {
//   const addColumn = () => {
//     const columns = content?.columns || [];
//     const newColumns = [
//       ...columns,
//       { 
//         title: '', 
//         content: '', 
//         imageUrl: '',
//         width: '1fr' // Default column width
//       }
//     ];
//     updateContent('columns', newColumns);
//   };
  
//   const updateColumn = (index, field, value) => {
//     const columns = [...(content?.columns || [])];
//     if (!columns[index]) return;
    
//     columns[index] = {
//       ...columns[index],
//       [field]: value
//     };
    
//     updateContent('columns', columns);
//   };
  
//   const removeColumn = (index) => {
//     const columns = [...(content?.columns || [])];
//     columns.splice(index, 1);
//     updateContent('columns', columns);
//   };
  
//   return (
//     <div>
//       <h3 className="font-bold text-lg mb-4">Columns Section</h3>
      
//       {/* Column layout settings */}
//       <div className="mb-4 p-3 border rounded bg-gray-50">
//         <h4 className="font-medium mb-2">Column Layout Settings</h4>
        
//         <div className="mb-2">
//           <label className="block text-sm mb-1">Gap Between Columns</label>
//           <input
//             type="text"
//             className="w-full p-2 border rounded"
//             placeholder="1rem or 16px"
//             value={content?.columnGap || '1rem'}
//             onChange={(e) => updateContent('columnGap', e.target.value)}
//           />
//         </div>
        
//         <div className="mb-2">
//           <label className="block text-sm mb-1">Columns Alignment</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.columnsAlignment || 'stretch'}
//             onChange={(e) => updateContent('columnsAlignment', e.target.value)}
//           >
//             <option value="stretch">Stretch (Default)</option>
//             <option value="flex-start">Top</option>
//             <option value="center">Center</option>
//             <option value="flex-end">Bottom</option>
//           </select>
//         </div>
        
//         <div className="mb-2">
//           <label className="block text-sm mb-1">Column Stacking (Mobile)</label>
//           <select
//             className="w-full p-2 border rounded"
//             value={content?.stackAt || 'sm'}
//             onChange={(e) => updateContent('stackAt', e.target.value)}
//           >
//             <option value="none">Don't stack (always show as columns)</option>
//             <option value="xs">Extra small screens</option>
//             <option value="sm">Small screens (default)</option>
//             <option value="md">Medium screens</option>
//             <option value="lg">Large screens</option>
//             <option value="xl">Extra large screens</option>
//           </select>
//         </div>
//       </div>
      
//       {/* Individual columns */}
//       <div className="mb-4">
//         {(content?.columns || []).map((column, index) => (
//           <div key={index} className="mb-4 p-3 border rounded">
//             <div className="flex justify-between items-center mb-2">
//               <h4 className="font-medium">Column {index + 1}</h4>
//               <button
//                 type="button"
//                 className="text-red-500 hover:text-red-700"
//                 onClick={() => removeColumn(index)}
//               >
//                 Remove
//               </button>
//             </div>
            
//             <div className="mb-2">
//               <label className="block text-sm mb-1">Title</label>
//               <input
//                 type="text"
//                 className="w-full p-2 border rounded"
//                 value={column.title || ''}
//                 onChange={(e) => updateColumn(index, 'title', e.target.value)}
//               />
//             </div>
            
//             <div className="mb-2">
//               <label className="block text-sm mb-1">Content</label>
//               <textarea
//                 className="w-full p-2 border rounded"
//                 rows="3"
//                 value={column.content || ''}
//                 onChange={(e) => updateColumn(index, 'content', e.target.value)}
//               ></textarea>
//             </div>
            
//             <div className="mb-2">
//               <label className="block text-sm mb-1">Image URL</label>
//               <input
//                 type="text"
//                 className="w-full p-2 border rounded"
//                 value={column.imageUrl || ''}
//                 onChange={(e) => updateColumn(index, 'imageUrl', e.target.value)}
//               />
//               {pageSlug && (
//                 <ImageUploader 
//                   onImageUpload={(path) => updateColumn(index, 'imageUrl', path)} 
//                   pageSlug={pageSlug} 
//                 />
//               )}
//             </div>
            
//             {column.imageUrl && (
//               <div className="mt-2 mb-2 p-2 border rounded">
//                 <img 
//                   src={column.imageUrl} 
//                   alt={`Column ${index + 1} image`}
//                   className="max-w-full h-auto max-h-32 mx-auto"
//                 />
//               </div>
//             )}
            
//             <div className="mb-2">
//               <label className="block text-sm mb-1">Column Width</label>
//               <select
//                 className="w-full p-2 border rounded"
//                 value={column.width || '1fr'}
//                 onChange={(e) => updateColumn(index, 'width', e.target.value)}
//               >
//                 <option value="1fr">Equal width (1fr)</option>
//                 <option value="2fr">Double width (2fr)</option>
//                 <option value="3fr">Triple width (3fr)</option>
//                 <option value="25%">25%</option>
//                 <option value="33%">33%</option>
//                 <option value="50%">50%</option>
//                 <option value="66%">66%</option>
//                 <option value="75%">75%</option>
//                 <option value="100%">100%</option>
//               </select>
//             </div>
            
//             <div className="mb-2">
//               <label className="block text-sm mb-1">Background Color</label>
//               <input
//                 type="text"
//                 className="w-full p-2 border rounded"
//                 placeholder="#ffffff or transparent"
//                 value={column.backgroundColor || ''}
//                 onChange={(e) => updateColumn(index, 'backgroundColor', e.target.value)}
//               />
//             </div>
            
//             <div className="mb-2">
//               <label className="block text-sm mb-1">Text Color</label>
//               <input
//                 type="text"
//                 className="w-full p-2 border rounded"
//                 placeholder="#000000"
//                 value={column.textColor || ''}
//                 onChange={(e) => updateColumn(index, 'textColor', e.target.value)}
//               />
//             </div>
            
//             <div className="mb-2">
//               <label className="block text-sm mb-1">Padding</label>
//               <input
//                 type="text"
//                 className="w-full p-2 border rounded"
//                 placeholder="1rem or 16px"
//                 value={column.padding || ''}
//                 onChange={(e) => updateColumn(index, 'padding', e.target.value)}
//               />
//             </div>
            
//             <div className="mb-2">
//               <label className="block text-sm mb-1">Alignment</label>
//               <select
//                 className="w-full p-2 border rounded"
//                 value={column.alignment || 'left'}
//                 onChange={(e) => updateColumn(index, 'alignment', e.target.value)}
//               >
//                 <option value="left">Left</option>
//                 <option value="center">Center</option>
//                 <option value="right">Right</option>
//               </select>
//             </div>
//           </div>
//         ))}
        
//         <button
//           type="button"
//           className="w-full p-2 border border-dashed rounded text-blue-500 hover:bg-blue-50"
//           onClick={addColumn}
//         >
//           + Add Column
//         </button>
//       </div>
//     </div>
//   );
// };

// export default ColumnsForm;







// // src/components/admin/page-management/section-forms/ColumnsForm.js
// import React from 'react';

// const ColumnsForm = ({ content, updateContent }) => {
//   // For simplicity, this is a placeholder
//   // In a real implementation, you would use a more sophisticated interface
//   // to add, remove, and edit columns
  
//   const addColumn = () => {
//     const columns = content?.columns || [];
//     const newColumns = [
//       ...columns,
//       { title: '', content: '', imageUrl: '' }
//     ];
//     updateContent('columns', newColumns);
//   };
  
//   const updateColumn = (index, field, value) => {
//     const columns = [...(content?.columns || [])];
//     if (!columns[index]) return;
    
//     columns[index] = {
//       ...columns[index],
//       [field]: value
//     };
    
//     updateContent('columns', columns);
//   };
  
//   const removeColumn = (index) => {
//     const columns = [...(content?.columns || [])];
//     columns.splice(index, 1);
//     updateContent('columns', columns);
//   };
  
//   return (
//     <div>
//       <h3 className="font-bold text-lg mb-4">Columns Section</h3>
      
//       <div className="mb-4">
//         {(content?.columns || []).map((column, index) => (
//           <div key={index} className="mb-4 p-3 border rounded">
//             <div className="flex justify-between items-center mb-2">
//               <h4 className="font-medium">Column {index + 1}</h4>
//               <button
//                 type="button"
//                 className="text-red-500 hover:text-red-700"
//                 onClick={() => removeColumn(index)}
//               >
//                 Remove
//               </button>
//             </div>
            
//             <div className="mb-2">
//               <label className="block text-sm mb-1">Title</label>
//               <input
//                 type="text"
//                 className="w-full p-2 border rounded"
//                 value={column.title || ''}
//                 onChange={(e) => updateColumn(index, 'title', e.target.value)}
//               />
//             </div>
            
//             <div className="mb-2">
//               <label className="block text-sm mb-1">Content</label>
//               <textarea
//                 className="w-full p-2 border rounded"
//                 rows="3"
//                 value={column.content || ''}
//                 onChange={(e) => updateColumn(index, 'content', e.target.value)}
//               ></textarea>
//             </div>
            
//             <div className="mb-2">
//               <label className="block text-sm mb-1">Image URL</label>
//               <input
//                 type="text"
//                 className="w-full p-2 border rounded"
//                 value={column.imageUrl || ''}
//                 onChange={(e) => updateColumn(index, 'imageUrl', e.target.value)}
//               />
//             </div>
//           </div>
//         ))}
        
//         <button
//           type="button"
//           className="w-full p-2 border border-dashed rounded text-blue-500 hover:bg-blue-50"
//           onClick={addColumn}
//         >
//           + Add Column
//         </button>
//       </div>
//     </div>
//   );
// };

// export default ColumnsForm;